import * as React from 'react';
import { useIntl } from 'react-intl';
import { AccessibilityInfo, Platform } from 'react-native';

import { ErrorRemovingLastFavoriteStoreModal } from 'components/modal-error-removing-last-favorite-store';
import { InternalStoreDiagnosticModal } from 'components/store-diagnostic-modal';
import { useFavoriteStore } from 'hooks/favorite-stores';

import { useClosedStoreEvent } from './hooks/use-closed-stores-event';
import { useDiagnostics } from './hooks/use-diagnostics';
import { useDistanceText } from './hooks/use-distance-text';
import { useErrorMessage } from './hooks/use-error-message';
import { useOpenClosedText } from './hooks/use-open-closed-text';
import { useServiceModeText } from './hooks/use-service-mode-text';
import { Wrapper } from './store-card.styled';
import { StoreCardView } from './store-card.view';
import type { StoreCardProps } from './types';
import {
  formatRestaurantPhysicalAddress,
  formatRestaurantPhysicalAddressForAccessibility,
} from './utils/format-restaurant-physical-address';

const FAVORITE_STORE_ANNOUNCEMENT_REGION_ID = 'favorite-store-announcement-region';

function announceFavoriteStoreChange(message: string) {
  if (Platform.OS !== 'web') {
    AccessibilityInfo.announceForAccessibility(message);
    return;
  }

  const region = getFavoriteStoreAnnouncementRegion();
  if (!region || typeof window === 'undefined') {
    return;
  }

  region.textContent = '';
  window.requestAnimationFrame(() => {
    region.textContent = message;
  });
}

function getFavoriteStoreAnnouncementRegion() {
  if (typeof document === 'undefined' || !document.body) {
    return null;
  }

  const existingRegion = document.getElementById(FAVORITE_STORE_ANNOUNCEMENT_REGION_ID);
  if (existingRegion instanceof HTMLElement) {
    return existingRegion;
  }

  const region = document.createElement('div');
  region.id = FAVORITE_STORE_ANNOUNCEMENT_REGION_ID;
  region.setAttribute('aria-atomic', 'true');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('role', 'status');
  Object.assign(region.style, {
    border: '0',
    clip: 'rect(0, 0, 0, 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: '0',
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px',
  });
  document.body.appendChild(region);
  return region;
}

/**
 * StoreCard container component. This component is intended to be used on the
 * store locator. Data orchestration should happen at this level while keeping
 * the view layer more presentational.
 */
const StoreCard: React.VFC<StoreCardProps> = ({
  testID,
  restaurant,
  onCardClick,
  isSelected,
  isSingleCardUI,
  isStoreLocatorFocusTarget,
  storeLocatorFocusRef,
  isFirst = false, // only used for logging purposes
}) => {
  const distanceText = useDistanceText({ restaurant });
  const errorMessage = useErrorMessage({ restaurant });
  const serviceModeText = useServiceModeText({ restaurant });
  const { formatMessage } = useIntl();

  const {
    data: diagnosticsData,
    showModal: showDiagnosticsModal,
    onClick: onDiagnosticsClick,
    onDismiss: showDiagnosticsModalDismiss,
  } = useDiagnostics({ restaurant });

  const {
    ErrorFavoriteStoreDialog,
    favStoreLoading,
    isFavorite,
    toggleFavorite: onFavoriteClick,
    hasErrorRemovingLastFavStore,
    onDismissErrorRemovingLastFavStoreModal,
  } = useFavoriteStore(restaurant._id ?? '', restaurant.number ?? '');

  const { isOpen, text } = useOpenClosedText(restaurant);

  const handleFavoriteClick = React.useCallback(async () => {
    const wasFavorite = isFavorite;
    const didUpdateFavorite = await onFavoriteClick();

    if (!didUpdateFavorite) {
      return;
    }

    const restaurantName = restaurant.name?.trim() || 'Restaurant';
    const message = wasFavorite
      ? formatMessage(
          {
            id: 'restaurantRemovedFromFavoritesAnnouncement',
            defaultMessage: '{restaurantName} removed from favorites.',
          },
          { restaurantName }
        )
      : formatMessage(
          {
            id: 'restaurantAddedToFavoritesAnnouncement',
            defaultMessage: '{restaurantName} added to favorites.',
          },
          { restaurantName }
        );

    announceFavoriteStoreChange(message);
  }, [formatMessage, isFavorite, onFavoriteClick, restaurant.name]);

  // This is only used to collect metrics regarding the fix for https://rbictg.atlassian.net/browse/FHS-4715
  // We can remove this hook once we confirm the fix solved the issue.
  useClosedStoreEvent({ isFirst, isOpen, restaurant });

  return (
    <Wrapper testID={testID}>
      <StoreCardView
        onCardClick={onCardClick}
        onDiagnosticsClick={onDiagnosticsClick}
        onFavoriteClick={handleFavoriteClick}
        isFavorite={isFavorite}
        isUpdatingFavorites={favStoreLoading}
        isSelected={isSelected}
        isSingleCardUI={isSingleCardUI}
        isStoreLocatorFocusTarget={isStoreLocatorFocusTarget}
        storeLocatorFocusRef={storeLocatorFocusRef}
        storeDetails={{
          body: serviceModeText,
          distanceText,
          errorMessage,
          ...(isOpen ? { openText: text } : { closedText: text }),
          title: restaurant.name,
          address: formatRestaurantPhysicalAddress(restaurant.physicalAddress),
          addressAccessibilityLabel: formatRestaurantPhysicalAddressForAccessibility(
            restaurant.physicalAddress
          ),
          isHalal: Boolean(restaurant.isHalal),
        }}
      />
      {showDiagnosticsModal && (
        <InternalStoreDiagnosticModal
          onModalDismiss={showDiagnosticsModalDismiss}
          storeDiagnosticData={diagnosticsData}
        />
      )}
      {hasErrorRemovingLastFavStore && (
        <ErrorRemovingLastFavoriteStoreModal
          isOpen={hasErrorRemovingLastFavStore}
          onDismiss={onDismissErrorRemovingLastFavStoreModal}
        />
      )}
      <ErrorFavoriteStoreDialog buttonLabel={formatMessage({ id: 'signUp' })} />
    </Wrapper>
  );
};

export default StoreCard;
